// src/components/routing/ZoneCommandCenter.js
// --- UPDATE (Field Ops · Zones v1) — NEW FILE ---------------------------------------------
// Zone / Constituency Command Center for the Field Operations page — everything happening in
// every zone, live: KPI strip, colour-coded operations map (zones, work orders, field staff),
// FlowSense AI (findings, dispatch suggestions with Assign, explain, ask), per-zone analytics
// table and a zone drawer (its jobs, its people, its suggestions).
// Data: useFieldOpsZones (REST overview + WebSocket field_ops_zone_update and the work-order /
// location events). Props let the page assign work (existing PUT /workorders/:id/assign) and
// open its management tabs.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Grid, HStack, Text, Badge, IconButton, Icon, Tooltip, SimpleGrid, Alert, AlertIcon, AlertDescription, Button, Switch, FormLabel,
    useColorModeValue, useDisclosure, useToast,
} from '@chakra-ui/react';
import { MdRefresh, MdMap, MdWifi, MdWifiOff } from 'react-icons/md';
import useFieldOpsZones from '../../hooks/useFieldOpsZones';
import ZoneOperationsMap from './ZoneOperationsMap';
import FieldOpsAiPanel from './FieldOpsAiPanel';
import { ZoneIntelTable, ZoneDetailDrawer } from './ZoneIntelPanels';
import { dash, ago } from './zoneGeo';

const KpiTile = memo(({ label, value, color, hint }) => {
    const bg = useColorModeValue('white', 'gray.800'); const border = useColorModeValue('gray.200', 'gray.700'); const muted = useColorModeValue('gray.500', 'gray.400');
    return (
        <Tooltip hasArrow label={hint} isDisabled={!hint}>
            <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" px={3} py={2} data-testid={`kpi-${label}`}>
                <Text fontSize="2xs" fontWeight="800" textTransform="uppercase" color={muted} noOfLines={1}>{label}</Text>
                <Text fontSize="xl" fontWeight="900" color={color} lineHeight="1.2">{value}</Text>
            </Box>
        </Tooltip>
    );
});
KpiTile.displayName = 'KpiTile';
KpiTile.propTypes = { label: PropTypes.string, value: PropTypes.node, color: PropTypes.string, hint: PropTypes.string };

const WS_LABEL = { connected: 'Live', connecting: 'Connecting…', reconnecting: 'Reconnecting…', waiting: 'Offline — retrying', disconnected: 'Offline', failed: 'Offline — retrying', error: 'Connection error', idle: 'Not connected' };

function ZoneCommandCenter({ onAssign, onOpenTab }) {
    const { data, isLoading, error, wsStatus, lastEvent, updatedAt, refresh, reload } = useFieldOpsZones();
    const [selectedZoneId, setSelectedZoneId] = useState(null);
    const [selectedTechId, setSelectedTechId] = useState(null);
    const [unzonedOnly, setUnzonedOnly] = useState(false);
    const [assigning, setAssigning] = useState(null);
    const drawer = useDisclosure();
    const toast = useToast();
    const card = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.500', 'gray.400');

    const zoneById = useMemo(() => new Map((data?.zones || []).map((z) => [String(z.id), z])), [data?.zones]);
    const selectedZone = selectedZoneId != null ? zoneById.get(String(selectedZoneId)) || null : null;

    const selectZone = useCallback((id) => { setUnzonedOnly(false); setSelectedZoneId(id); if (id != null) drawer.onOpen(); }, [drawer]);
    const openJob = useCallback((w) => { if (w.zone_id != null) setSelectedZoneId(w.zone_id); }, []);
    const openTech = useCallback((t) => { setSelectedTechId(t.id); if (t.zone_id != null) setSelectedZoneId(t.zone_id); }, []);

    const handleAssign = useCallback(async (d) => {
        if (!onAssign) return;
        setAssigning(d.work_order_id);
        try {
            await onAssign(d.work_order_id, d.tech_id);
            toast({ title: 'Work order assigned', description: `#${d.work_order_id} → ${d.tech_name}`, status: 'success', duration: 3000 });
            reload({ quiet: true });
        } catch (e) {
            toast({ title: 'Could not assign', description: e?.message || 'Assignment failed', status: 'error', duration: 5000 });
        } finally { setAssigning(null); }
    }, [onAssign, reload, toast]);

    const handleAction = useCallback((a) => {
        if (a.type === 'zone') selectZone(a.zone_id);
        else if (a.type === 'filter' && a.unzoned) { setSelectedZoneId(null); setUnzonedOnly(true); }
        else if (a.type === 'tab') onOpenTab?.(a.tab);
    }, [selectZone, onOpenTab]);

    const t = data?.totals;
    const live = wsStatus === 'connected';
    return (
        <Box bg={card} borderWidth="1px" borderColor={border} borderRadius="2xl" p={{ base: 3, md: 5 }} mb={5} position="relative" overflow="hidden" data-testid="zone-command-center">
            <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, teal.400, blue.500, purple.500)" />
            <HStack justify="space-between" mb={4} flexWrap="wrap" spacing={3}>
                <HStack spacing={3}>
                    <Box bgGradient="linear(to-br, teal.400, blue.600)" p={2.5} borderRadius="xl" boxShadow="md"><Icon as={MdMap} color="white" boxSize={5} /></Box>
                    <Box>
                        <Text fontWeight="900" fontSize="lg">Zones / Constituencies — live operations</Text>
                        <Text fontSize="xs" color={muted}>Every work order and field staff position placed in its zone · a zone and a constituency are the same thing</Text>
                    </Box>
                </HStack>
                <HStack spacing={2}>
                    <Tooltip hasArrow label={lastEvent ? `Last live event: ${lastEvent.type} ${ago(lastEvent.at.toISOString())}` : 'Listening for work-order and location events'}>
                        <Badge colorScheme={live ? 'green' : 'orange'} variant="subtle" px={2} py={1} borderRadius="full" display="flex" alignItems="center" gap={1} aria-label={`WebSocket ${WS_LABEL[wsStatus] || wsStatus}`}>
                            <Icon as={live ? MdWifi : MdWifiOff} />{WS_LABEL[wsStatus] || wsStatus}
                        </Badge>
                    </Tooltip>
                    <Text fontSize="xs" color={muted} display={{ base: 'none', md: 'block' }}>{updatedAt ? `Updated ${updatedAt.toLocaleTimeString()}` : ''}</Text>
                    <HStack spacing={1}>
                        <Switch size="sm" id="unzoned-only" isChecked={unzonedOnly} onChange={(e) => { setUnzonedOnly(e.target.checked); if (e.target.checked) setSelectedZoneId(null); }} colorScheme="orange" />
                        <FormLabel htmlFor="unzoned-only" mb={0} fontSize="xs">Outside zones only</FormLabel>
                    </HStack>
                    <Tooltip hasArrow label="Recompute now"><IconButton size="sm" variant="ghost" icon={<Icon as={MdRefresh} />} onClick={refresh} isLoading={isLoading} aria-label="Refresh zone data" /></Tooltip>
                </HStack>
            </HStack>

            {error && (
                <Alert status="warning" borderRadius="xl" mb={4}>
                    <AlertIcon /><AlertDescription fontSize="sm" flex={1}>{error}</AlertDescription>
                    <Button size="xs" onClick={() => reload()}>Retry</Button>
                </Alert>
            )}

            <SimpleGrid columns={{ base: 2, md: 4, xl: 8 }} spacing={2} mb={4}>
                <KpiTile label="Zones" value={dash(t?.zones)} hint="Zones / constituencies on the map" />
                <KpiTile label="Open jobs" value={dash(t?.open)} />
                <KpiTile label="Critical" value={dash(t?.critical)} color={t?.critical ? 'red.500' : undefined} />
                <KpiTile label="Overdue" value={dash(t?.overdue)} color={t?.overdue ? 'orange.500' : undefined} hint={t && t.overdue === null ? 'Work orders have no due-date column' : undefined} />
                <KpiTile label="Unassigned" value={dash(t?.unassigned)} />
                <KpiTile label="Staff online" value={t ? `${t.technicians_online}/${t.technicians}` : '—'} color="green.500" hint={data ? `Online = GPS update within ${data.schema?.online_window_minutes ?? 15} min` : undefined} />
                <KpiTile label="Jobs located" value={t ? `${t.located_work_orders}/${t.open}` : '—'} hint="Open jobs with coordinates" />
                <KpiTile label="Outside zones" value={dash(data?.unzoned?.work_orders)} color={data?.unzoned?.work_orders ? 'orange.500' : undefined} hint="Open jobs not inside any zone / constituency" />
            </SimpleGrid>

            <Grid templateColumns={{ base: '1fr', xl: '1.7fr 1fr' }} gap={4} mb={4}>
                <ZoneOperationsMap height="640px" data={data} selectedZoneId={selectedZoneId} onSelectZone={selectZone} onOpenJob={openJob} onOpenTech={openTech} selectedTechId={selectedTechId} showUnzonedOnly={unzonedOnly} />
                <FieldOpsAiPanel data={data} isLoading={isLoading} selectedZoneId={selectedZoneId} onAction={handleAction} onAssign={onAssign ? handleAssign : undefined} assigning={assigning} />
            </Grid>

            <ZoneIntelTable zones={data?.zones || []} selectedZoneId={selectedZoneId} onSelectZone={selectZone} isLoading={isLoading} />

            <ZoneDetailDrawer isOpen={drawer.isOpen && !!selectedZone} onClose={drawer.onClose} zone={selectedZone} data={data} onAssign={onAssign ? handleAssign : undefined} assigning={assigning} />
        </Box>
    );
}

ZoneCommandCenter.propTypes = { onAssign: PropTypes.func, onOpenTab: PropTypes.func };

export default memo(ZoneCommandCenter);
